import type MarkdownIt from "markdown-it";
import { MATH_INLINE_REGEX } from "@caliu/utils";

/**
 * markdown-it rules for LaTeX math, installed through tiptap-markdown's
 * `parse.setup(md)` hook (see `MathExtension`). Running as real parser
 * rules (instead of a post-parse DOM walk like highlights) matters because
 * LaTeX is full of markdown-significant characters: `x_i * y_j`, `\\`,
 * `\{`. A rule that claims the `$…$` span before `escape` / `emphasis` run
 * keeps the source byte-for-byte.
 *
 * Emits `<span data-type="inline-math" data-latex>` and
 * `<div data-type="block-math" data-latex>`, the DOM shapes
 * `@tiptap/extension-mathematics` parses.
 *
 * The delimiter rules are the shared contract in `@caliu/utils` (`math.ts`),
 * mirrored by the Swift editor. Change them there.
 */

type RuleInline = Parameters<MarkdownIt["inline"]["ruler"]["push"]>[1];
type RuleBlock = Parameters<MarkdownIt["block"]["ruler"]["push"]>[1];
type StateInline = Parameters<RuleInline>[0];
type StateBlock = Parameters<RuleBlock>[0];

const mathInline: RuleInline = (state: StateInline, silent: boolean) => {
  const { src, pos, posMax } = state;
  if (src.charCodeAt(pos) !== 0x24 /* $ */) return false;

  // The shared regex is global; anchor it at `pos` and reject a match that
  // starts later (the lookbehind still sees the char before `pos`).
  const re = new RegExp(MATH_INLINE_REGEX.source, "g");
  re.lastIndex = pos;
  const match = re.exec(src);
  if (!match || match.index !== pos) return false;
  if (pos + match[0].length > posMax) return false;

  if (!silent) {
    const token = state.push("math_inline", "span", 0);
    token.content = match[1];
    token.markup = "$";
  }
  state.pos = pos + match[0].length;
  return true;
};

const mathBlock: RuleBlock = (
  state: StateBlock,
  startLine: number,
  endLine: number,
  silent: boolean,
) => {
  let pos = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];

  // Indented 4+ → code, not ours.
  if (state.sCount[startLine] - state.blkIndent >= 4) return false;
  if (pos + 2 > max || state.src.slice(pos, pos + 2) !== "$$") return false;
  pos += 2;

  let firstLine = state.src.slice(pos, max).trim();
  if (silent) return true;

  let lastLine = "";
  let found = false;
  let nextLine = startLine;

  // Single-line form: `$$latex$$`.
  if (firstLine.length >= 2 && firstLine.endsWith("$$")) {
    firstLine = firstLine.slice(0, -2).trim();
    found = true;
  }

  while (!found) {
    nextLine += 1;
    if (nextLine >= endLine) break;
    pos = state.bMarks[nextLine] + state.tShift[nextLine];
    max = state.eMarks[nextLine];
    // A non-empty line that dedents past the block's indent ends the
    // container we're in (list item, blockquote) — the block is unclosed.
    if (pos < max && state.sCount[nextLine] < state.blkIndent) break;
    const line = state.src.slice(pos, max).trim();
    if (line.endsWith("$$")) {
      lastLine = line.slice(0, -2).trim();
      found = true;
    }
  }
  if (!found) return false;

  // `getLines` keeps the trailing newline of the last line; drop it so the
  // join below never doubles it up.
  const middle =
    nextLine > startLine
      ? state
          .getLines(startLine + 1, nextLine, state.tShift[startLine], true)
          .replace(/\n$/, "")
      : "";
  const content = [firstLine, middle, lastLine]
    .filter((part) => part.length > 0)
    .join("\n")
    .trim();

  state.line = nextLine + 1;
  const token = state.push("math_block", "div", 0);
  token.block = true;
  token.content = content;
  token.markup = "$$";
  token.map = [startLine, state.line];
  return true;
};

const installed = new WeakSet<MarkdownIt>();

/**
 * Install the math rules on a markdown-it instance. Idempotent: tiptap-markdown
 * calls every extension's `setup` on every parse, so a second call must not
 * stack duplicate rules.
 */
export function installMarkdownItMath(md: MarkdownIt): void {
  if (installed.has(md)) return;
  installed.add(md);

  md.inline.ruler.before("escape", "math_inline", mathInline);
  md.block.ruler.before("fence", "math_block", mathBlock, {
    alt: ["paragraph", "reference", "blockquote", "list"],
  });

  md.renderer.rules.math_inline = (tokens, idx) =>
    `<span data-type="inline-math" data-latex="${md.utils.escapeHtml(tokens[idx].content)}"></span>`;
  md.renderer.rules.math_block = (tokens, idx) =>
    `<div data-type="block-math" data-latex="${md.utils.escapeHtml(tokens[idx].content)}"></div>\n`;
}
